/**
 * R-14 T-088 — choose a role and a department to preview the app as that persona. Offered to
 * Administrators where the server allows it (never on PROD); start and stop are audited by the API.
 */
import { useState } from 'react';
import { useViewAs, useViewAsControl } from '@/app/experience/experience-api';
import { Button, Field, FormColumn, Modal, requestDrawerLeave, Select } from '@/kit';
import { personaName } from './ViewAsBanner';

export function ViewAsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  // the personas an Administrator may preview come with GET /api/auth/view-as (roles in business words)
  const state = useViewAs(open);
  const departments = state.data?.personas?.departments ?? [];
  const { start } = useViewAsControl();
  const [role, setRole] = useState<string | null>(null);
  const [dept, setDept] = useState<string | null>(null);
  const roles = state.data?.personas?.roles ?? [];
  const deptName = departments.find((d) => d.id === dept)?.name ?? null;
  const roleLabel = roles.find((r) => r.code === role)?.label;
  const who = role ? personaName(deptName, roleLabel) : null;
  const go = () => {
    if (!role) return;
    // starting resets every cached read, so an open record with unsaved edits is asked about first
    void requestDrawerLeave().then((ok) => ok && start.mutate({ role, departmentId: dept }, { onSuccess: onClose }));
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="View as"
      eyebrow="Preview another person's view"
      footerNote="Anything you record while viewing is saved as you. Starting and stopping are recorded."
      footerWhy={!role ? 'Choose a role first.' : start.error ? `Could not start: ${start.error.message}` : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={go} disabled={!role || start.isPending}>
            {who ? `View as ${who}` : 'View as'}
          </Button>
        </>
      }
    >
      <FormColumn>
        <Field label="Role" htmlFor="view-as-role">
          <Select id="view-as-role" aria-label="Role" value={role} onValueChange={setRole} options={roles.map((r) => ({ value: r.code, label: r.label }))} />
        </Field>
        <Field label="Department" htmlFor="view-as-dept" hint="The department this person belongs to.">
          <Select
            id="view-as-dept"
            aria-label="Department"
            value={dept}
            onValueChange={setDept}
            options={departments.map((d) => ({ value: d.id, label: d.name }))}
          />
        </Field>
      </FormColumn>
    </Modal>
  );
}
